<template>
  <div class="w-full space-y-6">
    <!-- Header -->
    <div class="flex justify-between items-center px-1">
      <h2 class="text-xl font-bold text-gray-800 dark:text-white">
        Documentos Anexos
      </h2>
      <ButtonDefault @click="openModal('set_anexo')">
        <ArrowUpTrayIcon class="shrink-0 size-4" />
        Cargar nuevo
      </ButtonDefault>
    </div>

    <!-- Lista de Anexos -->
    <div class="w-full">
      <ul>
        <li
          v-for="item in anexos"
          :key="item.cianId || 0"
          class="group relative"
        >
          <div
            class="absolute inset-0 bg-gradient-to-r from-transparent via-gray-50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-200 dark:via-gray-800"
          ></div>
          <div class="relative p-4 flex items-center justify-between">
            <div class="flex-1 min-w-0">
              <div class="flex items-center gap-x-3">
                <div class="shrink-0">
                  <span
                    class="inline-flex items-center justify-center size-8 rounded-full"
                    :class="
                      item.cianExt === 'pdf'
                        ? 'bg-red-100 text-red-500'
                        : 'bg-blue-100 text-blue-500'
                    "
                  >
                    <DocumentIcon
                      v-if="item.cianExt === 'pdf'"
                      class="size-5"
                    />
                    <PhotoIcon v-else class="size-5" />
                  </span>
                </div>
                <div class="flex-1 min-w-0">
                  <p
                    class="text-sm font-medium text-gray-900 dark:text-white truncate"
                  >
                    {{ item.cianNombre }}
                  </p>
                  <div class="flex items-center gap-x-2 text-xs text-gray-500">
                    <span>[{{ item.cianExt }}]</span>
                    <span>•</span>
                    <span>{{
                      item.tipo ? item.tipo.comaNombrelargo : 'No registrado'
                    }}</span>
                    <span>•</span>
                    <span>{{
                      formatearFechaEnEspanol(
                        item.cianFecha || new Date().toString()
                      )
                    }}</span>
                  </div>
                  <ul v-if="item.tecRelaciones?.length" class="mt-2 space-y-1">
                    <li
                      v-for="rel in item.tecRelaciones"
                      :key="rel.catId"
                      class="flex items-start gap-x-2"
                    >
                      <span class="shrink-0 size-1.5 rounded-full bg-blue-500 dark:bg-blue-400 mt-1.5"></span>
                      <span class="text-[11px] text-gray-700 dark:text-gray-300">{{ rel.tecnologia?.cittTechnologia }}</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
            <div class="flex items-center gap-x-3">
              <button
                v-if="item.cianId"
                class="inline-flex items-center gap-x-2 text-sm text-gray-400 hover:text-blue-600 transition-colors"
                title="Ver documento"
                @click="ver(item.cianId, item.cianExt || '')"
              >
                <EyeIcon class="size-4" />
              </button>
              <button
                v-if="item.cianId"
                :disabled="(item.tecRelaciones?.length ?? 0) > 0"
                :title="(item.tecRelaciones?.length ?? 0) > 0 ? 'No se puede eliminar: tiene servicios relacionados. Use Limpiar en la vista del laboratorio.' : 'Eliminar documento'"
                :class="(item.tecRelaciones?.length ?? 0) > 0 ? 'opacity-40 cursor-not-allowed' : 'hover:text-red-600'"
                class="inline-flex items-center gap-x-2 text-sm text-gray-400 transition-colors"
                @click="(item.tecRelaciones?.length ?? 0) === 0 && eliminarAnexo(item.cianId)"
              >
                <TrashIcon class="size-4" />
              </button>
            </div>
          </div>
          <div class="mx-4">
            <div
              class="border-t border-gray-400 dark:border-gray-400 w-[96%] mx-auto"
            ></div>
          </div>
        </li>
      </ul>

      <!-- Estado de carga -->
      <div class="px-1 py-4">
        <InfiniteLoading @infinite="load">
          <template #complete>
            <div class="text-center">
              <p
                v-if="anexos.length > 0"
                class="text-sm text-gray-500 dark:text-gray-400"
              >
                No hay más anexos para mostrar.
              </p>
              <p v-else class="text-sm text-gray-500 dark:text-gray-400">
                No se han cargado anexos aún.
              </p>
            </div>
          </template>
          <template #error>
            <p class="text-center text-sm text-red-500 dark:text-red-400">
              Error al cargar los anexos.
            </p>
          </template>
        </InfiniteLoading>
      </div>
    </div>

    <!-- Resto de modales -->
    <ModalDefault id="view_anexo" title="Vista de anexo" size="lg">
      <template #modal-body>
        <template v-if="anexo.cianExt == 'pdf'">
          <object>
            <embed
              type="text/html"
              width="100%"
              height="700"
              :src="'data:application/pdf;base64,' + anexo.base64"
            />
          </object>
        </template>
        <template v-else-if="anexo.base64">
          <img
            :src="anexo.base64"
            class="rounded-xl"
            width="100%"
            height="100%"
          />
        </template>
      </template>
      <template #modal-footer>
        <ButtonDefault
          v-if="anexo.cianId"
          color="red"
          @click="eliminarAnexo(anexo.cianId, true)">
            Eliminar
          </ButtonDefault>
      </template>
    </ModalDefault>
    <ModalDefault
      id="set_anexo"
      title="Agregar anexo"
      size="sm"
      :fnc-on-close="limpiarFormulario"
    >
      <template #modal-body>
        <div v-if="technologias.length > 0" class="mb-4 sm:mb-8 relative">
          <SelectDefault
            v-model="form.cittId"
            label="Tecnología"
            :options="technologias"
            :map-options="(item: ICitaTechnologiasSalud) => {
            return {
              optionText: item.cittTechnologia,
              optionValue: item.cittId
            }
          }
            "
          ></SelectDefault>
        </div>
        <div class="mb-4 sm:mb-8 relative">
          <InputDefault
            v-model="form.cianNombre"
            label="Nombre"
            type="text"
          ></InputDefault>
        </div>
        <div v-if="tipos" class="mb-4 sm:mb-8 relative">
          <SelectDefault
            v-model="form.cianTipo"
            label="Tipo anexo"
            :options="tipos.tipoanexos"
            :map-options="(item: IConfMaestra) => {
            return {
              optionText: item.comaNombrelargo,
              optionValue: item.comaId
            }
          }
            "
          ></SelectDefault>
        </div>
        <div class="mb-4 ml-1 sm:mb-8 relative">
          <label class="block text-sm"> Anexo</label>
          <span class="sr-only">Elige el archivo de cabecera</span>
          <input
            ref="fileInput"
            type="file"
            accept="image/*,.pdf"
            class="block w-full text-sm text-gray-500 file:me-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-blue-100 file:text-blue-600 hover:file:bg-blue-200 file:disabled:opacity-50 file:disabled:pointer-events-none dark:file:bg-blue-500 dark:hover:file:bg-blue-400 mt-1"
            @change="onFileChange"
          />
          <p v-if="errorArchivo" class="text-red-500 text-xs mt-1">{{ errorArchivo }}</p>
        </div>
      </template>
      <template #modal-footer>
        <ButtonDefault :is-loading="isSaving" @click="createAnexo">
          Guardar
        </ButtonDefault>
      </template>
    </ModalDefault>
  </div>
</template>
<script setup lang="ts">
import { ref, type Ref, onMounted } from 'vue'
import InfiniteLoading from 'v3-infinite-loading'
import {
  EyeIcon,
  ArrowUpTrayIcon,
  TrashIcon,
  DocumentIcon,
  PhotoIcon
} from '@heroicons/vue/24/outline'
import { openModal, closeModal } from '@/modules/Core/usings/generales'
import 'v3-infinite-loading/lib/style.css'
import { formatearFechaEnEspanol } from '@/modules/Core/usings/generales'
import type {
  ICitaAnexo,
  ICitaTechnologiasSalud
} from '@/modules/Asistencial/interfaces'
import type { IConfMaestra } from '@/modules/Configuracion/interfaces'
import { anexoService, maestraService } from '@/services'
import ButtonDefault from '@/modules/Core/components/general/ButtonDefault.vue'

const { coppId, technologias } = defineProps({
  coppId: {
    type: Number,
    default: 0
  },
  technologias: {
    type: Object,
    default: () => ({})
  }
})
let offset = 0
const anexos: Ref<ICitaAnexo[]> = ref([])
interface TipoAnexos {
  tipoanexos: IConfMaestra[]
}

const tipos: Ref<TipoAnexos | null> = ref(null)
const isSaving = ref(false)
const anexo: Ref<ICitaAnexo> = ref({} as ICitaAnexo)
const datosIniciales: ICitaAnexo = {
  copeId: coppId
} as ICitaAnexo
const form = ref({ ...datosIniciales })
const errorArchivo = ref('')
const fileInput = ref<HTMLInputElement | null>(null)
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const load = async ($state: any) => {
  const { data } = await anexoService.search({ offset: offset, copeId: coppId })
  if (data.body.rows.length < 5) $state.complete()
  else {
    $state.loaded()
  }
  anexos.value.push(...data.body.rows)
  offset++
}

const getTipos = async () => {
  const { data } = await maestraService.searchByType('TBL_TIPODEANEXOS')
  tipos.value = { tipoanexos: data.body }
}

const ver = async (id: number, tipo: string) => {
  window.notificacion.loading().circle()
  const { data } = await anexoService.getAnexe(id)
  window.notificacion.loading().remove()
  if (data.error) return
  anexo.value.cianId = id
  anexo.value.base64 = data.body as string
  anexo.value.cianExt = tipo
  openModal('view_anexo')
}

const createAnexo = async () => {
  if (!form.value.file) {
    errorArchivo.value = 'Debe seleccionar un archivo'
    return
  }

  isSaving.value = true
  window.notificacion.loading().circle()

  if (!form.value.file) return
  const filename = form.value.file.name
  form.value.cianMimeType = form.value.file.type
  form.value.cianExt = filename.split('.').pop()

  const formData = new FormData()
  formData.append('file', form.value.file, filename)
  if (form.value.cianId) formData.append('cianId', form.value.cianId.toString())
  formData.append('cianNombre', form.value.cianNombre)
  if (form.value.cianTipo)
    formData.append('cianTipo', form.value.cianTipo.toString())
  if (form.value.cittId) formData.append('cittId', form.value.cittId.toString())
  if (form.value.copeId) formData.append('copeId', form.value.copeId.toString())
  if (form.value.cianRuta) formData.append('cianRuta', form.value.cianRuta)
  if (form.value.cianExt) formData.append('cianExt', form.value.cianExt)
  formData.append('cianMimeType', form.value.cianMimeType)

  try {
    const { data } = !form.value.cianId
      ? await anexoService.postFile(`create`, formData).then(data => data)
      : await anexoService
          .putFile(`update/${form.value.cianId}`, formData)
          .then(data => data)

    anexos.value.push(data.body)
    closeModal('set_anexo')
    limpiarFormulario()
    window.notificacion.notificacion('success', 'Anexo cargado exitosamente')
  } catch (error) {
    console.error(error)
    window.notificacion.notificacion('failure', 'Error al cargar el anexo')
  } finally {
    isSaving.value = false
    window.notificacion.loading().remove()
  }
}

const eliminarAnexo = async (id: number, isModalOpen = false) => {
  try {
    const okCB = async () => {
      if (isModalOpen) closeModal('view_anexo')
      window.notificacion.loading().circle()
      try {
        const result = await anexoService.delete(id)
        if (result) {
          anexos.value = anexos.value.filter(e => e.cianId != id)
          form.value = { ...datosIniciales }
          window.notificacion.notificacion('success', 'Ha sido eliminado con exito.')
        }
      } finally {
        window.notificacion.loading().remove()
      }
    }
    window.notificacion.confirmacion(
      'Estas seguro?',
      'No podras revertir esto!',
      okCB,
      () => {}
    )
  } catch (error) {
    console.log(error)
  }
}

const onFileChange = (event: Event) => {
  errorArchivo.value = ''
  const input = event.target as HTMLInputElement
  const files = input.files

  if (files && files.length > 0) {
    const file = files[0]
    const fileType = file.type
    const fileSize = file.size

    // Validar tipo de archivo
    if (!fileType.startsWith('image/') && fileType !== 'application/pdf') {
      errorArchivo.value = 'Solo se permiten imágenes o archivos PDF'
      input.value = '' // Limpiar el input
      return
    }

    // Validar tamaño según el tipo
    const MAX_PDF_SIZE = 10 * 1024 * 1024 // 10MB en bytes
    const MAX_IMAGE_SIZE = 1 * 1024 * 1024 // 1MB en bytes

    if (fileType === 'application/pdf' && fileSize > MAX_PDF_SIZE) {
      errorArchivo.value = 'El archivo PDF no debe superar los 10MB de tamaño'
      input.value = ''
      return
    }

    if (fileType.startsWith('image/') && fileSize > MAX_IMAGE_SIZE) {
      errorArchivo.value = 'La imagen no debe superar 1MB de tamaño'
      input.value = ''
      return
    }

    // Si pasa todas las validaciones, asignar el archivo
    form.value.file = file
  } else {
    form.value.file = null
  }
}

const limpiarFormulario = () => {
  // Restablecer valores del formulario
  form.value = { ...datosIniciales }

  // Limpiar el input de archivo
  if (fileInput.value) {
    fileInput.value.value = ''
  }

  // Limpiar mensaje de error
  errorArchivo.value = ''
}

onMounted(async () => {
  await getTipos()
})
</script>
